import { StateCreator } from 'zustand';
import { useDocumentManagementStore } from '../documentManagement/documentManagement.store';
import { IInstitutionsFields, IInstitutionsSlice } from './types';
import { institutionsApi } from '@/API';
import { TInstitutionServerCamel } from '@/types/institutions';
import { convertKeys } from '@/utils/common';
import { LOCAL_STORAGE_KEYS } from '@/utils/constants';
import { isAxiosError } from 'axios';
import { EServerError } from '@/types';

const initState: IInstitutionsFields = {
  currentInstitution: null,
  institutions: [],
};

export const institutionsSlice: StateCreator<IInstitutionsSlice> = (
  set,
  get,
) => ({
  ...initState,

  is2FAEnabledForAnyInstitution: () =>
    get().institutions.some((inst) => inst.configuration?.tfa?.enabled),
  getCurrentInstitution: () => get().currentInstitution,

  getInstitutionById: (id) => {
    return (
      get().institutions.find((institution) => institution.id === id) ?? null
    );
  },

  getKioskById: (id: string) =>
    get().currentInstitution?.kiosks?.find((kiosk) => kiosk.id === id) ?? null,

  getService: (id: string) =>
    get().currentInstitution?.services?.find((spec) => spec.id === id) ?? null,

  getServiceName: (id: string) => {
    const result = get().getService(id);
    if (result) {
      return result.name;
    }
    return 'Unknown service';
  },

  getInstitutions: async () => {
    try {
      const response = await institutionsApi.getInstitutions();
      const rawInstitutions: TInstitutionServerCamel[] = convertKeys(
        response.data.data,
      );

      const result: TInstitutionServerCamel[] = rawInstitutions.map((item) => {
        return {
          ...item,
          phone: item.phone?.number ? item.phone : null,
          payment: {
            ...item.payment,
            enabled: Boolean(item.payment?.enabled),
            // Previously intervals could be an array, but now we only support number value
            // If it's an array or invalid type, default to undefined
            intervals: !isNaN(Number(item.payment?.intervals))
              ? Number(item.payment?.intervals)
              : undefined,
          },
          configuration: {
            ...item.configuration,
            enforceCountry: !!item.configuration?.enforceCountry,
            dispatcherOption: !!item.configuration?.dispatcherOption,
            repingDelay: item.configuration?.repingDelay ?? 60,
            repingTimes: item.configuration?.repingTimes ?? 5,
            waitingRoomOption: item.configuration?.waitingRoomOption ?? true,
            dispatcherTimer: item.configuration?.dispatcherTimer ?? 5,
            waitingRoomTimer: item.configuration?.waitingRoomTimer ?? 5,
            disablePatientScheduling:
              item.configuration?.disablePatientScheduling ?? false,
          },
          whitelabel: {
            ...item.whitelabel,
            enabled: Boolean(item.whitelabel?.enabled),
          },
        };
      });

      set({ institutions: result });
      return result;
    } catch (error) {
      console.error(error);
      set({ institutions: [], currentInstitution: null });
    }
    return [];
  },

  getRequiredPatientFields: () => {
    return Array.from(
      new Set(
        get().institutions.flatMap(
          ({ configuration }) => configuration?.requiredPatientFields ?? [],
        ),
      ),
    );
  },

  getRequiredProviderFields: () => {
    return Array.from(
      new Set(
        get().institutions.flatMap(
          ({ configuration }) => configuration?.requiredProviderFields ?? [],
        ),
      ),
    );
  },

  checkInstitutionExists: (id) =>
    get().institutions.some((inst) => inst.id === id),

  setCurrentInstitutionById: (id: string) => {
    const { institutions, currentInstitution } = get();
    const institution = institutions.find((inst) => inst.id === id);
    if (institution) {
      // If whitelabel is enabled, keep the current institution ID in localStorage.
      // This prevents unwanted redirects when this whitelabeled origin is visited again.
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.CURRENT_INSTITUTION_ID,
        currentInstitution?.whitelabel?.enabled ? currentInstitution.id : id,
      );

      set({ currentInstitution: institution });
      useDocumentManagementStore
        .getState()
        .setPackets(institution.formPackets ?? []);
      return;
    }
    console.error(`Institution with ID "${id}" not found.`);
  },

  updateCurrentInstitution: async (institution) => {
    if (!institution.id) throw new Error('Institution ID is undefined');
    const payloadInSnakeCase = convertKeys(institution, 'snake');
    const res = await institutionsApi.updateInstitution(
      institution.id,
      payloadInSnakeCase,
    );
    const rawInstitution = convertKeys(res.data.data);

    set({ currentInstitution: rawInstitution });
    return rawInstitution;
  },

  connectDisconnectDoseSpot: async (id, connect = true) => {
    const apiCall = connect
      ? institutionsApi.connectDoseSpot.bind(institutionsApi)
      : institutionsApi.disconnectDoseSpot.bind(institutionsApi);
    try {
      const res = await apiCall(id);
      const rawInstitution = convertKeys(res.data.data);
      set({ currentInstitution: rawInstitution });
    } catch (error) {
      console.error(error);
      if (isAxiosError(error)) {
        const errorWithDetails = new Error(
          error.response?.data.error || EServerError.UNKNOWN_ERROR,
        );
        errorWithDetails.cause = error.response?.data?.meta;
        throw errorWithDetails;
      }
      throw new Error(EServerError.UNKNOWN_ERROR);
    }
  },

  patchInstitution: async (id, payload) => {
    const { currentInstitution, institutions } = get();
    if (currentInstitution?.id !== id) {
      throw new Error('Cannot patch current institution');
    }
    try {
      const payloadInSnakeCase = convertKeys(payload, 'snake');
      const res = await institutionsApi.patchInstitution(
        id,
        payloadInSnakeCase,
      );
      const rawInstitution = convertKeys(res.data.data);

      set({ currentInstitution: rawInstitution });

      const updatedInstitutions = institutions.map((inst) =>
        inst.id === id ? rawInstitution : inst,
      );
      set({ institutions: updatedInstitutions });

      return rawInstitution;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  removeTemplateFromInstitution: async (templateId) => {
    const { currentInstitution } = get();
    if (!currentInstitution?.id) throw new Error('Institution not found');

    const updatedServices =
      currentInstitution.services?.map((service) => ({
        ...service,
        forms:
          service.forms?.filter((form) => form.templateId !== templateId) ?? [],
      })) ?? [];

    const updatedFormPackets =
      currentInstitution.formPackets?.map((packet) => ({
        ...packet,
        formIds:
          packet.formIds?.filter((formId) => formId !== templateId) ?? [],
      })) ?? [];

    await get().patchInstitution(currentInstitution.id, {
      services: updatedServices,
      formPackets: updatedFormPackets,
    });

    await useDocumentManagementStore
      .getState()
      .deleteFormBuilderTemplate(templateId);
  },

  updateInstitutionPackets: async (packets) => {
    const { currentInstitution } = get();
    if (!currentInstitution?.id) throw new Error('Institution not found');

    await get().patchInstitution(currentInstitution.id, {
      formPackets: packets,
    });
    useDocumentManagementStore.getState().setPackets(packets);
  },

  uploadInstitutionImage: async (formData: FormData): Promise<string> => {
    const response = await institutionsApi.uploadInstitutionImage(formData);
    return response.data.data;
  },

  clearInstitutions: () => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.CURRENT_INSTITUTION_ID);
    set(initState);
  },
});
